import {
  FileArchive,
  FileAudio,
  FileCode,
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
} from "@/components/icons"

export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return ""
  const units = ["B", "KB", "MB", "GB", "TB"]
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }
  const value = size % 1 === 0 ? size.toFixed(0) : size.toFixed(1)
  return `${value} ${units[unitIndex]}`
}

export function getReadableFileType(name?: string, mimeType?: string): string {
  const extension = (name || "").split(".").pop() || ""
  if (extension) return extension.toUpperCase()
  if (mimeType) {
    const main = mimeType.split("/")[0]
    return `${main.charAt(0).toUpperCase()}${main.slice(1)} file`
  }
  return ""
}

export function renderFileIcon(name?: string, mimeType?: string, isFolder?: boolean) {
  if (isFolder) {
    return <Folder aria-hidden className="mr-1 inline size-4" />
  }
  const extension = (name || "").split(".").pop()?.toLowerCase() || ""
  const type = (mimeType || "").toLowerCase()

  if (["doc", "docx"].includes(extension))
    return <FileText aria-hidden className="text-primary mr-1 inline size-4" />
  if (["xls", "xlsx", "csv"].includes(extension))
    return <FileSpreadsheet aria-hidden className="text-success mr-1 inline size-4" />
  if (["ppt", "pptx"].includes(extension))
    return <FileText aria-hidden className="text-primary-ink mr-1 inline size-4" />
  if (extension === "pdf" || type === "application/pdf")
    return <FileText aria-hidden className="text-destructive mr-1 inline size-4" />
  if (
    ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(extension) ||
    type.startsWith("image/")
  )
    return <FileImage aria-hidden className="mr-1 inline size-4" />
  if (["mp3", "wav", "aac", "flac", "ogg"].includes(extension) || type.startsWith("audio/"))
    return <FileAudio aria-hidden className="mr-1 inline size-4" />
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(extension) || type.startsWith("video/"))
    return <FileVideo aria-hidden className="mr-1 inline size-4" />
  if (["zip", "rar", "7z", "tar", "gz"].includes(extension))
    return <FileArchive aria-hidden className="mr-1 inline size-4" />
  if (["md", "txt", "rtf"].includes(extension))
    return <FileText aria-hidden className="mr-1 inline size-4" />
  if (
    [
      "js",
      "ts",
      "tsx",
      "json",
      "py",
      "java",
      "cs",
      "go",
      "rb",
      "php",
      "yaml",
      "yml",
      "xml",
      "html",
      "css",
    ].includes(extension)
  )
    return <FileCode aria-hidden className="mr-1 inline size-4" />
  return <FileIcon aria-hidden className="mr-1 inline size-4" />
}

export function getSourceLabel(file: { siteUrl?: string; webUrl?: string }): string {
  const urlStr = (file.siteUrl || file.webUrl || "").toLowerCase()
  if (!urlStr) return ""

  try {
    const urlObj = new URL(urlStr)
    const host = urlObj.host
    const pathname = urlObj.pathname || ""

    if (
      /^[^.]+-my\.sharepoint\.com$/.test(host) ||
      host === "my.sharepoint.com" ||
      pathname.includes("/personal/")
    ) {
      return "OneDrive"
    }
    if (host.endsWith(".sharepoint.com")) {
      return "SharePoint"
    }
    return ""
  } catch {
    return ""
  }
}

export function sortFiles<T>(
  arr: T[],
  getValue: (x: T) => string,
  direction: "asc" | "desc" = "asc"
): T[] {
  const copy = [...arr]
  copy.sort((a, b) => {
    const va = (getValue(a) || "").toLowerCase()
    const vb = (getValue(b) || "").toLowerCase()
    if (va < vb) return direction === "asc" ? -1 : 1
    if (va > vb) return direction === "asc" ? 1 : -1
    return 0
  })
  return copy
}
